import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  currentUser: {},
  rolesAndPermissions: {},
  // Org id that `rolesAndPermissions` was fetched for. Used to avoid acting on
  // stale permissions after an org switch before the new fetch completes.
  permissionsOrganisationId: null as string | null,
  accountType: '',
  currentOrg: null,
  isOrgListLoading: false,
  orgConfig: null,
  appConfig: null,
  currentSubscription: {},
  orgFeatureFlags: [] as string[],
  activeTourHint: null,
  currencyConfig: null,
  // Set when the active org is restricted (blacklisted or churned).
  restrictionModal: null,
  // Set when every branch the user could work in has been hidden or archived.
  noBranchAccess: false
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setCurrentUser: (
      state,
      { payload: { user } }: PayloadAction<{ user: any }>
    ) => ({
      ...state,
      currentUser: user
    }),
    setRolesAndPermissions: (
      state,
      {
        payload: { permissions, organisationId = null }
      }: PayloadAction<{ permissions: any; organisationId?: string | null }>
    ) => ({
      ...state,
      rolesAndPermissions: permissions,
      permissionsOrganisationId: organisationId
    }),
    setAccountType: (state, action: PayloadAction<any>) => {
      state.accountType = action.payload;
    },
    setCurrentOrg: (state, action: PayloadAction<any>) => {
      state.currentOrg = action.payload;
    },
    setOrgListLoading: (state, action: PayloadAction<any>) => {
      state.isOrgListLoading = action.payload.isOrgListLoading;
    },
    setOrgConfig: (state, action: PayloadAction<any>) => {
      state.orgConfig = action.payload;
    },
    setAppConfig: (state, action: PayloadAction<any>) => {
      state.appConfig = action.payload;
    },
    setCurrentSubscription: (state, action: PayloadAction<any>) => {
      state.currentSubscription = action.payload;
    },
    setActiveTourHint: (state, action: PayloadAction<any>) => {
      state.activeTourHint = action.payload;
    },
    setOrgFeatureFlags: (state, action: PayloadAction<any>) => {
      state.orgFeatureFlags = action.payload;
    },
    setCurrencyConfig: (state, action: PayloadAction<any>) => {
      state.currencyConfig = action.payload;
    },
    setRestrictionModal: (state, action: PayloadAction<any>) => {
      state.restrictionModal = action.payload;
    },
    setNoBranchAccess: (state, action: PayloadAction<boolean>) => {
      state.noBranchAccess = action.payload;
    },
    resetUserState: () => initialState
  }
});

export const {
  setCurrentUser,
  setRolesAndPermissions,
  setAccountType,
  setCurrentOrg,
  setOrgListLoading,
  setOrgConfig,
  setAppConfig,
  setCurrentSubscription,
  setOrgFeatureFlags,
  setCurrencyConfig,
  setRestrictionModal,
  setNoBranchAccess,
  resetUserState,
  setActiveTourHint
} = userSlice.actions;
export default userSlice.reducer;
